import React, { useEffect } from 'react'
import { RouteProps, useHistory } from 'react-router-dom'
import FullScreenLoading from '@/components/FullScreenLoading'
import SentryRoute from '@/components/SentryRoute'
import { ERoles } from '@/schemaTypes'
import { useAppSelector } from '@/store'
import { hasMinRole } from '@/utils/roles'

type RoleGuardedRouteProps = RouteProps & {
  /** Lowest role (see EMPLOYEE_ROLE_HIERARCHY) that may open this route. */
  minRole: ERoles
}

/**
 * A SentryRoute that only renders for employees holding at least `minRole`.
 * Everyone else is sent back to /inApp. This only hides pages — the API
 * enforces the same hierarchy via @Permissions on every resolver.
 */
const RoleGuardedRoute = ({
  minRole,
  ...routeProps
}: RoleGuardedRouteProps) => {
  const currentUser = useAppSelector((state) => state.user.current)
  const history = useHistory()
  const isAllowed = hasMinRole(currentUser, minRole)

  useEffect(() => {
    if (currentUser && !isAllowed) {
      history.replace('/inApp')
    }
  }, [currentUser, isAllowed, history])

  if (!currentUser) {
    return <FullScreenLoading />
  }

  if (!isAllowed) {
    return null
  }

  return <SentryRoute {...routeProps} />
}

export default RoleGuardedRoute
